import React, { useEffect } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import useFormName from '../../../../Hooks/useFormName';
import { joinClasses } from '../../../../Utils/className.util';
import ErrorMessage from '../ErrorMessage';
import Input, { InputProps } from '../Input';
import TopFieldContent from '../TopFieldContent';
import styles from './NumberField.module.scss';
import { isString, isNumber } from 'lodash';
import CharactersCount from '../CharactersCount/CharactersCount';
import InputWrapper from '../InputWrapper/InputWrapper';

const DECIMAL_REGEX = /^\d*([.,]?)\d*$/;
const INTEGER_REGEX = /^\d*$/;

interface InputFieldProps extends InputProps {
	name: string;
	label?: string;
	isRequired?: boolean;
	className?: string;
	useWrapper?: boolean;
	disabled?: boolean;
	charactersLimit?: number;
	showCharCounter?: boolean;
	isWrapped?: boolean;
	integerOnly?: boolean;
	minValue?: number;
}

const NumberField = ({
	name,
	label,
	isRequired,
	className,
	useWrapper = true,
	disabled,
	charactersLimit,
	showCharCounter = true,
	isWrapped,
	integerOnly = false,
	minValue,
	...inputProps
}: InputFieldProps) => {
	const { fieldName } = useFormName({ name });

	const { control } = useFormContext();

	const {
		field,
		fieldState: { error },
	} = useController({
		control,
		name: fieldName,
		defaultValue: '',
	});

	useEffect(() => {
		if (!isString(field.value) && !isNumber(field.value)) {
			field.onChange('');
		}
	}, [field.value]);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value.replace(',', '.');

		if (integerOnly) {
			if (!INTEGER_REGEX.test(value)) {
				return;
			}
		} else {
			if (!DECIMAL_REGEX.test(value)) {
				return;
			}
		}

		if (value === '') {
			field.onChange('');
			return;
		}

		const numericValue = Number(value);

		if (
			!Number.isNaN(numericValue) &&
			minValue !== undefined &&
			numericValue < minValue
		) {
			return;
		}

		field.onChange(value);
	};

	const component = (
		<div
			className={joinClasses([
				className,
				useWrapper ? styles.wrapper : undefined,
			])}>
			{label && <TopFieldContent label={label} isRequired={isRequired} />}
			<Input
				hasError={Boolean(error?.message)}
				{...inputProps}
				{...field}
				type='text'
				inputMode={integerOnly ? 'numeric' : 'decimal'}
				pattern={integerOnly ? '^\\d*$' : '^\\d*([.,]?)\\d*$'}
				disabled={disabled}
				onChange={handleChange}
				onFocus={event =>
					// @ts-ignore
					event.target.addEventListener('wheel', (event: React.FocusEvent) => {
						event.preventDefault();
					})
				}
				maxLength={charactersLimit}
			/>
			{error?.message && <ErrorMessage tx={error.message} />}
			{charactersLimit && showCharCounter && (
				<CharactersCount limit={charactersLimit} value={field.value} />
			)}
		</div>
	);

	if (isWrapped) {
		return <InputWrapper>{component}</InputWrapper>;
	}

	return component;
};

export default NumberField;
